import React, { useState, useMemo, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { ProductsContext } from '../contexts/ProductsContext';
import ProductGridItem from './ProductGridItem';
import { MAX_CONTENT_WIDTH, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_PAGE } from '../constants/layout';
import SortPicker, { sortProducts } from '../components/SortPicker';

export default function SearchView() {
  const [searchParams] = useSearchParams();
  const { products, getCoverImage, loading } = useContext(ProductsContext);

  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [sortBy, setSortBy] = useState('name_asc');

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    const filtered = products.filter(p => {
      const name = (p.productName || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });

    return sortProducts(filtered, sortBy);
  }, [products, query, sortBy]);

  return (
    <div
      style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '24px 12px 48px',
        boxSizing: 'border-box',
      }}
    >
      {/* Results header */}
      {query.trim() && !loading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <p
            style={{
              fontSize: '0.9rem',
              color: 'var(--color-text-secondary)',
              fontWeight: 600,
              margin: 0,
            }}
          >
            {results.length === 0
              ? `No results for \u201c${query.trim()}\u201d`
              : `${results.length} result${results.length === 1 ? '' : 's'} for \u201c${query.trim()}\u201d`}
          </p>

          <SortPicker value={sortBy} onChange={setSortBy} />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Loading…</p>
      )}

      {/* Results grid */}
      {!loading && results.length > 0 && (
        <div className="product-grid">
          {results.map(product => (
            <ProductGridItem
              key={product.id}
              product={product}
              coverImage={getCoverImage(product)}
              onClick={() => navigate(buildProductPath(product))}
              isNew={(() => {
                if (!product?.createdDate) return false;
                const created = product.createdDate.toDate ? product.createdDate.toDate() : new Date(product.createdDate);
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                return created > thirtyDaysAgo;
              })()}
            />
          ))}
        </div>
      )}
      <style jsx>{`
        .product-grid {
          display: grid;
          width: 100%;
          gap: ${SPACING_BUTTON};
          grid-template-columns: repeat(4, 1fr);
        }
        @media (min-width: 1400px) {
          .product-grid { grid-template-columns: repeat(5, 1fr); }
        }
        @media (min-width: 1000px) and (max-width: 1399px) {
          .product-grid { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 999px) {
          .product-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 600px) {
          .product-grid { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>

      {/* Empty state — no query */}
      {!query.trim() && !loading && (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          Enter a search term above to find products.
        </p>
      )}
    </div>
  );
}
